/** Every change to reading progress is SAID, with a way back.
 *
 *  `progressMoment` (services/progress/readingProgress.ts) fires for each one:
 *  a chapter counted because the reader reached its end or listened it
 *  through, a mark or unmark by hand, a Reading Plan day ticked or opened
 *  again. Each becomes one toast naming what changed (and any plan day it
 *  completed or reopened) with Undo on it, wherever the reader is: listening
 *  can finish a chapter while they are on another tab entirely.
 *
 *  Progress that arrives from the reader's other devices changes this screen
 *  without them doing anything here, so that is said too. */
import { chaptersLabel } from '../services/progress/progressModel';
import { dayLabel, type DayRef } from '../services/progress/planAgreement';
import { progressMoment, type ProgressMoment } from '../services/progress/readingProgress';
import { progressJustAdopted } from '../services/progress/progressSync';
import { toastAbout } from './widgets';
import { markShown } from '../services/progress/progressMarks';

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** The other plan days a moment moved, one sentence each, never the day the
 *  moment is about (its head already says that one).
 *
 *  Two plans can share a name: a preset started twice, or a shared copy beside
 *  the reader's own. Ticking Day 1 of one counts Matthew 1-5, which completes
 *  Day 1 of the other, and the toast said "Day 1 of Life Of Jesus is done"
 *  twice. The head now names its plan by LABEL ("Day 1 of Life Of Jesus (2)",
 *  `planNames.ts`), and a day in a plan with the same stored name is "your
 *  other" plan's; the same day done in several same-named plans is said once,
 *  with how many. */
function dayNews(m: ProgressMoment): string[] {
  const groups = new Map<string, { refs: DayRef[]; verb: string; other: boolean }>();
  const gather = (refs: DayRef[], verb: string): void => {
    for (const d of refs) {
      if (m.day && d.planId === m.day.planId && d.dayId === m.day.dayId) continue;
      const other = !!m.day && d.baseName === m.day.baseName && d.planId !== m.day.planId;
      const key = JSON.stringify([verb, other, d.baseName, d.dayNumber]);
      const g = groups.get(key);
      if (g) g.refs.push(d);
      else groups.set(key, { refs: [d], verb, other });
    }
  };
  gather(m.completed, 'is done');
  gather(m.reopened, 'is open again');
  return [...groups.values()].map(({ refs, verb, other }) => {
    const { baseName, dayNumber } = refs[0];
    const k = refs.length;
    if (other) return `Day ${dayNumber} ${verb} in your ${k === 1 ? 'other' : `${k} other`} ${baseName} ${plural(k, 'plan', 'plans')} too`;
    if (k > 1) return `Day ${dayNumber} ${verb} in ${k === 2 ? 'both' : `all ${k}`} of your ${baseName} plans`;
    return `${dayLabel(refs[0])} ${verb}`;
  });
}

/** "A", "A and B", "A, B and C" */
function listOf(parts: string[]): string {
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] ?? '';
}

/** Why each chapter an un-ticked day asks for still counts, one sentence per
 *  reason: the reader's own reading, or the other done days that still ask
 *  for it, each named by its label so "Life Of Jesus" and "Life Of Jesus (2)"
 *  are never a guess. Without it, un-ticking a day whose chapters stay read
 *  looks like the un-tick did not work. */
function keptNews(m: ProgressMoment): string[] {
  return m.kept.map((g) => {
    const n = g.chapters.length;
    const still = `${chaptersLabel(g.chapters)} still ${plural(n, 'counts', 'count')} as read`;
    const them = plural(n, 'it', 'them');
    switch (g.via) {
      case 'read': return `${still}: you read ${them} to the end`;
      case 'listen': return `${still}: you listened ${them} through`;
      case 'mark': return `${still}: you marked ${them} as read yourself`;
      default: return `${still}: ${listOf(g.by.map(dayLabel))} ${plural(g.by.length, 'is', 'are')} still done`;
    }
  });
}

/** The sentence for one moment. Exported for the smoke suite. */
export function momentMessage(m: ProgressMoment): string {
  const what = chaptersLabel(m.chapters);
  const n = m.chapters.length;
  const extra = [...keptNews(m), ...dayNews(m)];
  const tail = extra.length ? `. ${extra.join('. ')}.` : '.';
  switch (m.kind) {
    case 'counted':
      if (m.via === 'listen') return `${what} ${plural(n, 'counts', 'count')} as read: you listened ${plural(n, 'it', 'them')} through${tail}`;
      if (m.via === 'read') return `${what} ${plural(n, 'counts', 'count')} as read: you read ${plural(n, 'it', 'them')} to the end${tail}`;
      return `${what} marked as read${tail}`;
    case 'unmarked':
      return `${what} no longer ${plural(n, 'counts', 'count')} as read${tail}`;
    case 'day-done':
      return m.day
        ? `${dayLabel(m.day)} is done${n ? `, and ${what} ${plural(n, 'counts', 'count')} as read` : ''}${tail}`
        : `Day done${tail}`;
    case 'day-reopened':
      return m.day
        ? `${dayLabel(m.day)} is open again${n ? `, and ${what} no longer ${plural(n, 'counts', 'count')} as read` : ''}${tail}`
        : `Day open again${tail}`;
    default:
      return `Reading progress updated${tail}`;
  }
}

/** What an Undo put back, said because it can land on a screen that shows
 *  nothing of it (a chapter listened through while the reader is on Home).
 *  A tick or un-tick names its day too: the day is half of what came back.
 *  Exported for the smoke suite. */
export function undoMessage(m: ProgressMoment): string {
  const good = m.kind === 'counted' || m.kind === 'day-done';
  const n = m.chapters.length;
  // Whole sentences, so a translation can reorder them (services/i18n).
  const chapters = !n ? ''
    : good ? `${chaptersLabel(m.chapters)} no longer ${plural(n, 'counts', 'count')} as read`
      : `${chaptersLabel(m.chapters)} ${plural(n, 'counts', 'count')} again as read`;
  if (m.day && (m.kind === 'day-done' || m.kind === 'day-reopened')) {
    const and = chapters ? `, and ${chapters}` : '';
    return m.kind === 'day-done' ? `Undone: ${dayLabel(m.day)} is open again${and}.` : `Undone: ${dayLabel(m.day)} is done again${and}.`;
  }
  return chapters ? `Undone: ${chapters}.` : 'Undone.';
}

/** Long enough to read: an un-tick that says what left and what stayed runs
 *  to thirty words, which the flat 6.5 seconds cut off mid-sentence. */
function readingTime(text: string, floor: number): number {
  return Math.max(floor, text.split(/\s+/).length * 330);
}

let started = false;

export function initProgressToasts(): void {
  if (started) return;
  started = true;
  progressMoment.subscribe((m) => {
    if (!m) return;
    // What the APP counted is said only when the reader asked for it (the
    // `toasts` mark, off by default). What they did by hand is always
    // answered: that toast, with its Undo, is the reply to their own press.
    if (m.auto && !markShown('toasts')) return;
    const good = m.kind === 'counted' || m.kind === 'day-done';
    const said = momentMessage(m);
    toastAbout('progress', said, good ? 'success' : 'info', readingTime(said, 6500), {
      label: 'Undo',
      run: () => {
        m.undo();
        const back = undoMessage(m);
        toastAbout('progress', back, 'info', readingTime(back, 2600));
      },
    });
  });
  progressJustAdopted.subscribe((a) => {
    if (!a || !markShown('toasts')) return;
    const parts: string[] = [];
    if (a.read) parts.push(`${a.read} ${plural(a.read, 'chapter', 'chapters')} read on another device now ${plural(a.read, 'counts', 'count')} here`);
    if (a.unread) parts.push(`${a.unread} ${plural(a.unread, 'chapter was', 'chapters were')} unmarked on another device`);
    toastAbout('progress', `Reading progress from your TDG Account: ${parts.join(', and ')}.`, 'info', 5000);
  });
}
